import type { ReactNode } from 'react';
import type { LicenseDto, TenantStatus } from '@order-pilot/shared';

export type BadgeTone = 'neutral' | 'success' | 'danger' | 'warning' | 'accent';

const TONE_CLASS: Record<BadgeTone, string> = {
  neutral: 'bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200',
  success: 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/20',
  danger: 'bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-600/20',
  warning: 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-600/20',
  accent: 'bg-brand-50 text-brand-800 ring-1 ring-inset ring-brand-700/20',
};

const DOT_CLASS: Record<BadgeTone, string> = {
  neutral: 'bg-slate-400',
  success: 'bg-emerald-500',
  danger: 'bg-rose-500',
  warning: 'bg-amber-500',
  accent: 'bg-brand-600',
};

export interface BadgeProps {
  tone?: BadgeTone;
  dot?: boolean;
  children: ReactNode;
}

export function Badge({ tone = 'neutral', dot = false, children }: BadgeProps) {
  return (
    <span className={`badge ${TONE_CLASS[tone]}`}>
      {dot ? <span className={`h-1.5 w-1.5 rounded-full ${DOT_CLASS[tone]}`} aria-hidden="true" /> : null}
      {children}
    </span>
  );
}

export function TenantStatusBadge({ status }: { status: TenantStatus }) {
  return (
    <Badge tone={status === 'ACTIVE' ? 'success' : 'neutral'} dot>
      {status === 'ACTIVE' ? 'Active' : 'Disabled'}
    </Badge>
  );
}

/**
 * A device's SYNC state, not its trading state.
 *
 * "Disconnected" rather than "Revoked" because a revoked LICENCE stops a shop
 * trading, while a revoked device only stops it uploading. One word for both, on
 * a page that shows both, invites someone to read a stopped shop into a till
 * that is running fine — or to reach for the wrong control.
 *
 * Three states, not two. A till that has never called home used to read
 * "Active", which is a claim rather than a fact: registering a row in a database
 * says nothing about whether a POS exists at the other end. Reading "Never
 * connected" instead means the badge only says Active once a real till has
 * actually authenticated and pushed.
 */
export function DeviceStatusBadge({
  revokedAt,
  lastSeenAt,
}: {
  revokedAt: string | null;
  /** Null until this till's first successful sync. */
  lastSeenAt: string | null;
}) {
  if (revokedAt) {
    return (
      <Badge tone="danger" dot>
        Disconnected
      </Badge>
    );
  }
  if (!lastSeenAt) {
    return (
      <Badge tone="neutral" dot>
        Never connected
      </Badge>
    );
  }
  return (
    <Badge tone="success" dot>
      Active
    </Badge>
  );
}

/**
 * Licence state as an operator needs to read it.
 *
 * "Expired" is derived from the date rather than trusting `status`, because that
 * column only flips when a till refreshes. A till that has gone quiet would
 * otherwise still read as Active long after it stopped working — which is
 * exactly the till you opened the console to find.
 */
export function LicenseStatusBadge({ license }: { license: LicenseDto }) {
  if (license.revokedAt) {
    return (
      <Badge tone="danger" dot>
        Revoked
      </Badge>
    );
  }
  // Before the expiry check: a paused licence usually still has term left, so
  // the expiry branch would not catch it, and "Active" would be a lie.
  if (license.suspendedAt) {
    return (
      <Badge tone="warning" dot>
        Paused
      </Badge>
    );
  }
  if (license.status === 'PENDING') {
    return (
      <Badge tone="neutral" dot>
        Awaiting activation
      </Badge>
    );
  }
  if (license.expiresAt && new Date(license.expiresAt).getTime() < Date.now()) {
    return (
      <Badge tone="danger" dot>
        Expired
      </Badge>
    );
  }
  return (
    <Badge tone="success" dot>
      Active
    </Badge>
  );
}
